/**
 * Dashboard — account / licence information panel.
 *
 * Two sections:
 * 1. Account Overview — License Type, Expiry Date (data-feed validity, with
 *    days-remaining countdown), and App Version.
 * 2. Premium Strategies — every indicator licensed to this account (from
 *    `/metadata` → `indicatorList` / `indicatorExpiryDtMap`), each with its
 *    own expiry date/time and remaining-days countdown. Searchable and
 *    filterable by status (Active / Expiring Soon / Expired).
 */
import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { getColorTheme } from "@service/local-storage-handler";
import { IndicatorListItem } from "@store/metadata-slice";
import { normalizeIndicatorKey } from "../../indicators/engine/indicator-license";
import { daysUntilExpiryDay, parseExpiryDay } from "../../utils/license-expiry";
import "./Dashboard.scss";

type Urgency = "ok" | "warning" | "critical" | "expired";
type StatusBucket = "active" | "expiring" | "expired" | "unknown";

interface ExpiryMeta {
  dateStr: string;
  diffDays: number;
  urgency: Urgency;
  remainingLabel: string;
}

interface StrategyRow {
  key: string;
  name: string;
  meta: ExpiryMeta | null;
  bucket: StatusBucket;
}

const LicenseIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="db-svg-icon">
    <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M19 3H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2V5a2 2 0 00-2-2z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CalendarIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="db-svg-icon">
    <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    <line x1="16" y1="2" x2="16" y2="6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <line x1="8" y1="2" x2="8" y2="6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <line x1="3" y1="10" x2="21" y2="10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const VersionIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="db-svg-icon">
    <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    <polyline points="3.27 6.96 12 12.01 20.73 6.96" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    <line x1="12" y1="22.08" x2="12" y2="12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const ShieldIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="db-svg-icon">
    <path d="M12 3l7 3v6c0 4.4-3 8.2-7 9-4-0.8-7-4.6-7-9V6l7-3z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M9.5 12l1.8 1.8L15 10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const SearchIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="db-search-icon">
    <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="1.8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const ClearIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const StatusIcon: React.FC<{ bucket: StatusBucket }> = ({ bucket }) => {
  if (bucket === "expired") {
    return (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="db-svg-icon">
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
        <line x1="9" y1="9" x2="15" y2="15" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <line x1="15" y1="9" x2="9" y2="15" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    );
  }
  if (bucket === "expiring") {
    return (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="db-svg-icon">
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
        <path d="M12 7v5l3.5 2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="db-svg-icon">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
      <path d="M8.5 12.5l2.5 2.5 5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

/** Compute date formatting + remaining-days countdown for a raw expiry value. */
const describeExpiry = (raw?: string): ExpiryMeta | null => {
  // The backend sends a date only — parsed as local midnight and compared at
  // day granularity, so a license expiring today is not "expired" at 05:30.
  const expiryDay = parseExpiryDay(raw);
  if (!expiryDay) return null;

  const diffDays = daysUntilExpiryDay(raw);
  if (diffDays === null) return null;

  const pad = (n: number) => String(n).padStart(2, "0");
  const dateStr = `${pad(expiryDay.getDate())}-${pad(expiryDay.getMonth() + 1)}-${expiryDay.getFullYear()}`;

  let urgency: Urgency;
  let remainingLabel: string;
  if (diffDays < 0) {
    urgency = "expired";
    remainingLabel = `Expired ${Math.abs(diffDays)} day${Math.abs(diffDays) !== 1 ? "s" : ""} ago`;
  } else if (diffDays === 0) {
    urgency = "critical";
    remainingLabel = "Expires today";
  } else if (diffDays === 1) {
    urgency = "critical";
    remainingLabel = "1 day remaining";
  } else if (diffDays <= 7) {
    urgency = "warning";
    remainingLabel = `${diffDays} days remaining`;
  } else {
    urgency = "ok";
    remainingLabel = `${diffDays} days remaining`;
  }

  return { dateStr, diffDays, urgency, remainingLabel };
};

const bucketOf = (urgency: Urgency): StatusBucket => {
  if (urgency === "expired") return "expired";
  if (urgency === "warning" || urgency === "critical") return "expiring";
  return "active";
};

const FILTERS: { key: "all" | StatusBucket; label: string }[] = [
  { key: "all", label: "All" },
  { key: "active", label: "Active" },
  { key: "expiring", label: "Expiring Soon" },
  { key: "expired", label: "Expired" },
];

const Dashboard: React.FC = () => {
  const themeFromStore = useSelector(({ theme }: any) => theme.theme);
  const theme = themeFromStore || getColorTheme() || "dark";
  const { dashboardState, indicatorState } = useSelector(({ metadata }: any) => metadata);

  const appVersion = process.env.REACT_APP_VERSION || "1.0.0";

  const [strategySearch, setStrategySearch] = useState("");
  const [strategyFilter, setStrategyFilter] = useState<"all" | StatusBucket>("all");

  /* ── Overall license / data-feed expiry ──────────────────── */
  const expiryInfo = useMemo(
    () => describeExpiry(dashboardState.licenseExpiryDt),
    [dashboardState.licenseExpiryDt]
  );

  const licenseLabel = dashboardState.licenseType
    ? dashboardState.licenseType.charAt(0).toUpperCase() +
      dashboardState.licenseType.slice(1).toLowerCase()
    : "—";

  const isDemo = dashboardState.licenseType?.toLowerCase() === "demo";

  /* ── Premium Strategies license rows ──────────────────────── */
  const indicatorList: Array<string | IndicatorListItem> = indicatorState?.indicatorList ?? [];
  const indicatorExpiryDtMap: Record<string, string> = indicatorState?.indicatorExpiryDtMap ?? {};

  const strategyRows: StrategyRow[] = useMemo(() => {
    const normalizedExpiryEntries = Object.entries(indicatorExpiryDtMap || {});
    return indicatorList
      .map((item, idx) => {
        const id = typeof item === "string" ? "" : item?.id || "";
        const name = typeof item === "string" ? item : item?.name || "Unnamed Strategy";

        const rawExpiry =
          (id && indicatorExpiryDtMap?.[id]) ||
          normalizedExpiryEntries.find(
            ([key]) => normalizeIndicatorKey(key) === normalizeIndicatorKey(id || name)
          )?.[1];

        const meta = describeExpiry(rawExpiry);
        return {
          key: id || `${name}-${idx}`,
          name,
          meta,
          bucket: meta ? bucketOf(meta.urgency) : ("unknown" as StatusBucket),
        };
      })
      .sort((a, b) => {
        const da = a.meta?.diffDays ?? Number.POSITIVE_INFINITY;
        const db = b.meta?.diffDays ?? Number.POSITIVE_INFINITY;
        return da - db;
      });
  }, [indicatorList, indicatorExpiryDtMap]);

  const strategyCounts = useMemo(
    () => ({
      all: strategyRows.length,
      active: strategyRows.filter((r) => r.bucket === "active").length,
      expiring: strategyRows.filter((r) => r.bucket === "expiring").length,
      expired: strategyRows.filter((r) => r.bucket === "expired").length,
    }),
    [strategyRows]
  );

  const filteredStrategyRows = useMemo(() => {
    const term = strategySearch.trim().toLowerCase();
    return strategyRows.filter((row) => {
      if (strategyFilter !== "all" && row.bucket !== strategyFilter) return false;
      if (term && !row.name.toLowerCase().includes(term)) return false;
      return true;
    });
  }, [strategyRows, strategyFilter, strategySearch]);

  return (
    <div className={`db-root ${theme}`}>
      {/* ── Background glow ── */}
      <div className="db-bg-glow db-bg-glow--left" />
      <div className="db-bg-glow db-bg-glow--right" />

      {/* ── Header ── */}
      <header className="db-header">
        <p className="db-header__greeting">Welcome back</p>
        <h1 className="db-header__name">
          {dashboardState.userName || "User"}
        </h1>
        <p className="db-header__sub">Account Overview</p>
        <div className="db-header__divider" />
      </header>

      {/* ── Cards ── */}
      <div className="db-cards">

        {/* License Type */}
        <div className={`db-card db-card--license${isDemo ? " db-card--demo" : ""}`}>
          <div className="db-card__top-bar" />
          <div className="db-card__inner">
            <div className="db-card__icon-wrap db-card__icon-wrap--license">
              <LicenseIcon />
            </div>
            <div className="db-card__body">
              <span className="db-card__label">License Type</span>
              <span className="db-card__value">{licenseLabel}</span>
            </div>
          </div>
          <span className={`db-card__badge ${isDemo ? "db-card__badge--demo" : "db-card__badge--active"}`}>
            {isDemo ? "DEMO" : "ACTIVE"}
          </span>
          <div className="db-card__glare" />
        </div>

        {/* Expiry Date / Data Feed Validity */}
        <div className={`db-card db-card--expiry${expiryInfo ? ` db-card--expiry-${expiryInfo.urgency}` : ""}`}>
          <div className="db-card__top-bar" />
          <div className="db-card__inner">
            <div className={`db-card__icon-wrap db-card__icon-wrap--expiry${expiryInfo ? ` db-card__icon-wrap--expiry-${expiryInfo.urgency}` : ""}`}>
              <CalendarIcon />
            </div>
            <div className="db-card__body">
              <span className="db-card__label">Data Feed Validity</span>
              {expiryInfo ? (
                <>
                  <span className="db-card__value">{expiryInfo.dateStr}</span>
                  <span className={`db-card__countdown db-card__countdown--${expiryInfo.urgency}`}>
                    {expiryInfo.urgency !== "expired" && (
                      <span className="db-card__dot db-card__dot--pulse" />
                    )}
                    {expiryInfo.remainingLabel}
                  </span>
                </>
              ) : (
                <span className="db-card__value db-card__value--muted">—</span>
              )}
            </div>
          </div>
          <div className="db-card__glare" />
        </div>

        {/* App Version */}
        <div className="db-card db-card--version">
          <div className="db-card__top-bar" />
          <div className="db-card__inner">
            <div className="db-card__icon-wrap db-card__icon-wrap--version">
              <VersionIcon />
            </div>
            <div className="db-card__body">
              <span className="db-card__label">App Version</span>
              <span className="db-card__value db-card__value--mono">v{appVersion}</span>
            </div>
          </div>
          <span className="db-card__badge db-card__badge--version">LATEST</span>
          <div className="db-card__glare" />
        </div>

      </div>

      {/* ── Premium Strategies ── */}
      <section className="db-strategies">
        <div className="db-strategies__header">
          <div className="db-strategies__title-wrap">
            <div className="db-strategies__title-icon">
              <ShieldIcon />
            </div>
            <div>
              <h2 className="db-strategies__title">Premium Strategies</h2>
              <p className="db-strategies__subtitle">
                {strategyRows.length} licensed indicator{strategyRows.length !== 1 ? "s" : ""}
              </p>
            </div>
          </div>

          <div className="db-strategies__search">
            <SearchIcon />
            <input
              type="text"
              placeholder="Search strategies..."
              value={strategySearch}
              onChange={(e) => setStrategySearch(e.target.value)}
            />
            {strategySearch.length > 0 && (
              <button
                type="button"
                className="db-strategies__search-clear"
                onClick={() => setStrategySearch("")}
                title="Clear search"
                aria-label="Clear search"
              >
                <ClearIcon />
              </button>
            )}
          </div>
        </div>

        <div className="db-strategies__filters">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              className={`db-filter-chip db-filter-chip--${f.key}${strategyFilter === f.key ? " active" : ""}`}
              onClick={() => setStrategyFilter(f.key)}
            >
              {f.label}
              <span className="db-filter-chip__count">{strategyCounts[f.key]}</span>
            </button>
          ))}
        </div>

        {isDemo && strategyRows.length > 0 && (
          <div className="db-strategies__note">
            Demo license grants access to all strategies regardless of the expiry dates shown below.
          </div>
        )}

        {filteredStrategyRows.length > 0 ? (
          <div className="db-strategies__grid">
            {filteredStrategyRows.map((row) => (
              <div key={row.key} className={`strategy-card strategy-card--${row.bucket}`}>
                <div className={`strategy-card__icon strategy-card__icon--${row.bucket}`}>
                  <StatusIcon bucket={row.bucket} />
                </div>
                <div className="strategy-card__body">
                  <span className="strategy-card__name" title={row.name}>{row.name}</span>
                  {row.meta ? (
                    <>
                      <span className="strategy-card__date">
                        {row.meta.dateStr}
                      </span>
                      <span className={`strategy-card__remaining strategy-card__remaining--${row.meta.urgency}`}>
                        {row.meta.urgency !== "expired" && <span className="strategy-card__dot" />}
                        {row.meta.remainingLabel}
                      </span>
                    </>
                  ) : (
                    <span className="strategy-card__date strategy-card__date--muted">No expiry data</span>
                  )}
                </div>
                <span className={`strategy-card__badge strategy-card__badge--${row.bucket}`}>
                  {row.bucket === "active" ? "Active" : row.bucket === "expiring" ? "Expiring" : row.bucket === "expired" ? "Expired" : "N/A"}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className="db-strategies__empty">
            {strategyRows.length === 0
              ? "No premium strategies are licensed for this account."
              : "No strategies match your search or filter."}
          </div>
        )}
      </section>
    </div>
  );
};

export default Dashboard;
